'use client';

import { motion } from 'framer-motion';
import { useWordleStats } from '@/modules/shared/hooks/useWordleStats';

export function StatsContent({
  isPerfectWin,
  isHardcore = false,
  storageNamespace,
  strings,
  refreshKey,
}: {
  isPerfectWin: boolean;
  isHardcore?: boolean;
  storageNamespace?: string;
  strings: {
    gamesLabelPlural: string;
    gamesLabelSingular: string;
    winRateLabel: string;
    currentStreakLabel: string;
    bestStreakLabel: string;
    performanceTitle: string;
    lossLabel: string;
    timeTitle: string;
    bestTimeLabel: string;
    avgTimeLabel: string;
    worstTimeLabel: string;
  };
  refreshKey?: number;
}) {
  const stats = useWordleStats(storageNamespace, refreshKey);
  if (!stats) return null;

  const max = Math.max(...Object.values(stats.distribution));

  return (
    <>
      <motion.div
        className="grid grid-cols-4 gap-2 sm:gap-3 mb-5 sm:mb-6"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: {},
          visible: { transition: { staggerChildren: 0.08 } },
        }}
      >
        <StatBox
          label={
            stats.totalGames > 1
              ? strings.gamesLabelPlural
              : strings.gamesLabelSingular
          }
          value={stats.totalGames}
          isPerfectWin={isPerfectWin}
          isHardcore={isHardcore}
        />
        <StatBox
          label={strings.winRateLabel}
          value={stats.winRate}
          isPerfectWin={isPerfectWin}
          isHardcore={isHardcore}
        />
        <StatBox
          label={strings.currentStreakLabel}
          value={stats.currentStreak}
          isPerfectWin={isPerfectWin}
          isHardcore={isHardcore}
        />
        <StatBox
          label={strings.bestStreakLabel}
          value={stats.bestStreak}
          isPerfectWin={isPerfectWin}
          isHardcore={isHardcore}
        />
      </motion.div>

      <h3 className="mb-2 text-sm sm:text-base font-semibold">
        {strings.performanceTitle}
      </h3>

      <div className="space-y-2">
        {[1, 2, 3, 4, 5, 6].map(n => (
          <PerformanceBar
            key={n}
            label={n}
            value={stats.distribution[n as 1 | 2 | 3 | 4 | 5 | 6]}
            max={max}
            isPerfectWin={isPerfectWin}
            isHardcore={isHardcore}
          />
        ))}

        <PerformanceBar
          label={strings.lossLabel}
          value={stats.distribution.lost}
          max={max}
          isPerfectWin={isPerfectWin}
          isHardcore={isHardcore}
          isLoss
        />
      </div>

      {stats.timeStats && (
        <>
          <h3 className="mb-2 mt-5 text-sm sm:text-base font-semibold">
            {strings.timeTitle}
          </h3>
          <motion.div
            className="grid grid-cols-3 gap-2 sm:gap-3"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.08 } },
            }}
          >
            <TimeBox
              label={strings.bestTimeLabel}
              ms={stats.timeStats.bestMs}
              tone="best"
            />
            <TimeBox
              label={strings.avgTimeLabel}
              ms={stats.timeStats.avgMs}
              tone="avg"
            />
            <TimeBox
              label={strings.worstTimeLabel}
              ms={stats.timeStats.worstMs}
              tone="worst"
            />
          </motion.div>
        </>
      )}
    </>
  );
}

function formatMs(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function TimeBox({
  label,
  ms,
  tone,
}: {
  label: string;
  ms: number;
  tone: 'best' | 'avg' | 'worst';
}) {
  // Plus de temps restant = meilleure perf → vert ; moins = pire → rouge.
  const valueClass =
    tone === 'best'
      ? 'text-emerald-400'
      : tone === 'worst'
      ? 'text-red-400'
      : 'text-neutral-100';

  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, scale: 0.9, y: 6 },
        visible: { opacity: 1, scale: 1, y: 0 },
      }}
      transition={{ type: 'spring', stiffness: 280, damping: 22 }}
      whileHover={{ y: -3 }}
      className="relative overflow-hidden rounded-lg sm:rounded-xl bg-gradient-to-br from-neutral-800 to-neutral-900 px-2 sm:px-4 py-3 sm:py-4 text-center"
    >
      <div
        className={`relative z-10 font-mono text-lg sm:text-2xl font-extrabold tabular-nums ${valueClass}`}
      >
        {formatMs(ms)}
      </div>
      <div className="relative z-10 mt-1 text-[9px] sm:text-[11px] uppercase tracking-widest text-neutral-400">
        {label}
      </div>
    </motion.div>
  );
}

function StatBox({
  label,
  value,
  isPerfectWin,
  isHardcore,
}: {
  label: string;
  value: number;
  isPerfectWin?: boolean;
  isHardcore?: boolean;
}) {
  return (
    <motion.div
      variants={{
        hidden: { opacity: 0, scale: 0.9, y: 6 },
        visible: { opacity: 1, scale: 1, y: 0 },
      }}
      transition={{ type: 'spring', stiffness: 280, damping: 22 }}
      whileHover={{ y: -3 }}
      className={`
        relative overflow-hidden rounded-lg sm:rounded-xl
        bg-gradient-to-br from-neutral-800 to-neutral-900
        px-2 sm:px-4 py-3 sm:py-4
        text-center
        ${
          isPerfectWin
            ? 'ring-1 ring-yellow-400/40'
            : isHardcore
            ? 'ring-1 ring-red-500/40'
            : ''
        }
      `}
    >
      {isPerfectWin ? (
        <div className="absolute inset-0 bg-yellow-400/10 blur-xl" />
      ) : isHardcore ? (
        <div className="absolute inset-0 bg-red-500/10 blur-xl" />
      ) : null}

      <div
        className={`
          relative z-10
          text-xl sm:text-2xl
          font-extrabold
          ${
            isPerfectWin
              ? 'text-transparent bg-clip-text bg-gradient-to-b from-yellow-200 to-yellow-400'
              : isHardcore
              ? 'text-red-400'
              : 'text-green-400'
          }
        `}
      >
        {value}
      </div>

      <div
        className="
          relative z-10 mt-1
          text-[9px] sm:text-[11px]
          uppercase tracking-widest
          text-neutral-400
        "
      >
        {label}
      </div>
    </motion.div>
  );
}

function PerformanceBar({
  label,
  value,
  max,
  isPerfectWin,
  isHardcore,
  isLoss,
}: {
  label: number | string;
  value: number;
  max: number;
  isPerfectWin?: boolean;
  isHardcore?: boolean;
  isLoss?: boolean;
}) {
  const width = max === 0 ? 0 : (value / max) * 100;

  return (
    <div className="flex items-center gap-2 sm:gap-3">
      <span className="w-5 sm:w-6 text-xs sm:text-sm text-neutral-300">
        {label}
      </span>

      <div className="relative flex-1 h-6 rounded-md bg-black/40 shadow-inner overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${width}%` }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          className={`
            absolute inset-y-0 left-0 rounded-md
            ${
              isLoss
                ? 'bg-gradient-to-r from-neutral-500 to-neutral-400'
                : isPerfectWin
                ? 'bg-gradient-to-r from-yellow-300 to-yellow-400'
                : isHardcore
                ? 'bg-gradient-to-r from-red-600 to-red-400'
                : 'bg-gradient-to-r from-green-500 to-emerald-400'
            }
          `}
        />

        <div className="absolute inset-0 flex items-center justify-end pr-2">
          <span className="rounded bg-black/40 px-2 py-0.5 text-[10px] sm:text-xs font-medium text-white">
            {value}
          </span>
        </div>
      </div>
    </div>
  );
}
